import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Close } from '@mui/icons-material';
import { CityContext } from '../../ContextApi/cityContext';
import { APP_READY_EVENT, NO_PROMO_PATHS, cityPickerBlocking, hiddenRecently, isInApp, rememberHidden } from './appEnv';
import './AppPromo.css';

// "יש לנו אפליקציה": חלון קטן מתחתית המסך (לא מסך מלא - Google מורידה בדירוג על intrusive interstitials).
// - רק במובייל (Android / iOS לפי ה-User-Agent), ולא בתוך שום WebView (isInApp: גם הגרסאות הישנות של
//   האפליקציה, וגם הדפדפנים הפנימיים של פייסבוק / אינסטגרם).
// - לא מיד: אחרי SHOW_DELAY או במעבר לעמוד השני, מה שקודם. isInApp נבדק שוב ממש לפני ההצגה,
//   כי window.AshdodApp מוזרק רק אחרי הטעינה. האירוע 'ashdodapp' מסתיר.
// - סגירה (X / "לא עכשיו" / הכפתור הראשי) -> לא מוצג שוב HIDE_DAYS ימים (localStorage).
// - לא בעמודי הזמנה ותשלום, ולא כשבורר העיר פתוח.
// בנוסף מעדכן את app-argument של הבאנר של Safari בכל מעבר עמוד (התגית נוספת ב-public/index.html).

const APP_ID_IOS = '6471904204';
const ANDROID_PKG = 'com.asdodmenu.app'; // בכוונה בלי h - זה המזהה ב-Google Play
const SITE_HOST = 'www.ashdodmenu.co.il';
const STORE_KEY = 'amAppPromoHiddenAt';
const SHOW_DELAY = 6000;
// הפופ-אפ כבוי זמנית: הקישור לאפליקציה מוביל למקום שבור. עדכון app-argument של Safari ממשיך לעבוד.
const PROMO_ENABLED = false;

const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';
const isAndroid = /Android/i.test(ua);
// iPadOS מזדהה כ-Macintosh, ומובחן לפי מסך מגע
const isIOS = /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1);

const androidLink = (path) =>
    `intent://${SITE_HOST}${path}#Intent;scheme=https;package=${ANDROID_PKG};` +
    `S.browser_fallback_url=${encodeURIComponent(`https://play.google.com/store/apps/details?id=${ANDROID_PKG}`)};end`;

const AppPromo = () => {
    const location = useLocation();
    const cityCtx = useContext(CityContext);
    const [due, setDue] = useState(false); // עבר הזמן או שעברו עמוד
    const [hidden, setHidden] = useState(() => hiddenRecently(STORE_KEY));
    const [appReady, setAppReady] = useState(false); // הגיע 'ashdodapp' - אנחנו בתוך האפליקציה
    const firstPath = useRef(location.pathname);

    const path = location.pathname + location.search + location.hash;

    // app-argument של הבאנר של Safari - הכתובת הנוכחית
    useEffect(() => {
        if (isInApp()) return;
        const meta = document.querySelector('meta[name="apple-itunes-app"]');
        if (meta) meta.setAttribute('content', `app-id=${APP_ID_IOS}, app-argument=${window.location.href}`);
    }, [path]);

    // אחרי כמה שניות
    useEffect(() => {
        if (!(isAndroid || isIOS)) return;
        const t = setTimeout(() => setDue(true), SHOW_DELAY);
        return () => clearTimeout(t);
    }, []);

    // או במעבר לעמוד השני
    useEffect(() => {
        if (location.pathname !== firstPath.current) setDue(true);
    }, [location.pathname]);

    useEffect(() => {
        const onApp = () => setAppReady(true);
        window.addEventListener(APP_READY_EVENT, onApp);
        return () => window.removeEventListener(APP_READY_EVENT, onApp);
    }, []);

    if (!PROMO_ENABLED || !(isAndroid || isIOS) || !due || hidden || appReady) return null;
    // נבדק כאן, ממש לפני ההצגה
    if (isInApp()) return null;
    if (NO_PROMO_PATHS.some(p => location.pathname.startsWith(p))) return null;
    if (cityPickerBlocking(cityCtx)) return null;

    const close = () => {
        rememberHidden(STORE_KEY);
        setHidden(true);
    };

    const href = isAndroid ? androidLink(path) : `https://apps.apple.com/app/id${APP_ID_IOS}`;

    return (
        <div className="appPromo" role="dialog" aria-label="האפליקציה של אשדוד בתפריט" dir="rtl">
            <button className="appPromoClose" onClick={close} aria-label="סגור">
                <Close fontSize="small" />
            </button>
            <div className="appPromoTitle">יש לנו אפליקציה!</div>
            <div className="appPromoTxt">הזמנות מהירות יותר ומנות בלעדיות לאפליקציה</div>
            <div className="appPromoBtns">
                <a className="appPromoMain" href={href} onClick={close}
                    {...(isAndroid ? {} : { target: '_blank', rel: 'noopener noreferrer' })}>
                    {isAndroid ? 'פתח באפליקציה' : 'הורד מה-App Store'}
                </a>
                <button className="appPromoLater" onClick={close}>לא עכשיו</button>
            </div>
        </div>
    );
};

export default AppPromo;
